 /**
 * Prize Pop Only Layout Component
 * Renders only the prize pop section when campaignOnly.type === 'prizePop' is set.
 *
 * Supports:
 * - campaignOnly: { type: 'prizePop', campaignID?: string }
 *   - If campaignID is provided, navigates carousel to that specific campaign
 *   - If campaignID is not found or not provided, falls back to first campaign
 *
 * Minimal layout with just prize pop content
 */

import React, { useCallback, memo } from 'react';
import { SECTION_NAMES } from '@repo/common';

import { useCampaigns, useCampaignActions, useCampaignsStore } from 'features/campaigns/store';
import { PRIZE_POP_CAMPAIGN_TYPE, CampaignTypeProvider } from 'features/campaigns';
import { ViewSwitcher } from 'features/navigation';

import { useAppStore } from '../../../store/app/appStore';
import { useCampaignSelection } from '../hooks/useCampaignSelection';

import styles from '../styles/CampaignOnlyLayout.module.css';

interface PrizePopOnlyLayoutProps {
  campaignID?: string;
}

const PrizePopOnlyLayout: React.FC<PrizePopOnlyLayoutProps> = ({ campaignID }) => {
  // Get settings from app store
  const campaignOnly = useAppStore((state) => state.settings.campaignOnly);

  // Show layout if campaignOnly.type='prizePop'
  const showPrizePopOnly = campaignOnly?.type === PRIZE_POP_CAMPAIGN_TYPE;

  // Get campaign state
  const selectedCampaignId = useCampaignsStore((state) => state.getSelectedCampaignId(PRIZE_POP_CAMPAIGN_TYPE));
  const campaigns = useCampaigns(PRIZE_POP_CAMPAIGN_TYPE) as { id: string }[];
  const { setSelectedCampaignId } = useCampaignActions();
  const setActivePrizePop = useCallback(
    (id: string) => setSelectedCampaignId(id, PRIZE_POP_CAMPAIGN_TYPE),
    [setSelectedCampaignId],
  );

  // Use shared campaign selection hook
  useCampaignSelection({
    shouldShow: showPrizePopOnly,
    campaignID,
    items: campaigns,
    activeItemId: selectedCampaignId ?? undefined,
    setActiveItem: setActivePrizePop,
    sectionName: SECTION_NAMES.PRIZE_POP,
    debugPrefix: 'PrizePopOnly',
    getItemId: (campaign) => campaign.id.toString(),
  });

  // Don't render anything if campaignOnly.type='prizePop' is not enabled
  if (!showPrizePopOnly) {
    return null;
  }

  return (
    <CampaignTypeProvider type={PRIZE_POP_CAMPAIGN_TYPE}>
      <div className={styles.campaignOnlyContainer}>
        <div className={styles.contentContainer}>
          {/* Only show prize pop content via ViewSwitcher */}
          <ViewSwitcher showTournamentNavigation={false} campaingOnlyView={true} />
        </div>
      </div>
    </CampaignTypeProvider>
  );
};

export default memo(PrizePopOnlyLayout);